import styled from "styled-components/macro";
import { line, radii, space, text } from "src/theme/ui";
import { colors } from "src/theme/colors";
import { MOBILE, IPAD } from "src/utils/constants/media";

/* The summary panel. Two ems of side padding measured twenty-six pixels at
   this font size, so its two rows sat inset from every other line on the
   screen. */
export const AlertInfo = styled.div`
  display: flex;
  align-items: center;
  background-color: ${colors.gray[25]};
  border: 1px solid ${line.hairline};
  padding: 12px 14px;
  border-radius: ${radii.lg}px;
`;

/**
 * The summary's two rows.
 *
 * Twenty-five pixels of padding at each end, on top of the panel's own — so
 * fifty pixels of blank paper wrapped fifty pixels of text and the panel came
 * out a hundred and twenty-five pixels tall to hold two lines. The panel pads
 * itself; these rows only need the gap between them.
 */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space.md}px;
  width: 100%;

  @media (max-width: ${MOBILE}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }
`;

/* A reading's name — muted ink at the product's label size, not black at
   half opacity and 19px. */
export const SpanName = styled.span`
  font-size: 12.5px;
  color: ${colors.gray[600]};
`;

export const SpanValue = styled.span`
  ${text.numeric}
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  text-transform: capitalize;
  white-space: nowrap;
  color: ${colors.gray[900]};
`;

/* The review step's body. `space-around` on a full-height column spread its
   three parts to the corners of whatever contained them, which in a dialog
   meant the gaps grew with the dialog rather than staying what they were set
   to. */
export const DivCom = styled.div`
  display: flex;
  flex-direction: column;
`;

/* The action row. Five ems of margin above and below put eighty pixels of
   blank paper between the last field and the button that acts on it, and the
   20px of vertical padding forced on every button in here overrode the
   product's own control heights. */
export const ButtonContainer = styled.div<{
  position?: string;
  /** In a dialog the actions are a footer BAND, not a row in the body. */
  $band?: boolean;
}>`
  margin: ${space.xl}px 0 0;
  display: flex;
  align-items: center;
  justify-content: ${({ position }) => position || "flex-end"};
  gap: ${space.sm}px;

  /* Full-bleed to the dialog's edges, ruled off and sunk — the same footer
     every other dialog in the product finishes with. Inside the body the two
     buttons read as one more form row, and a dialog whose actions are part of
     its content gives the reader nowhere settled to look for them. */
  ${({ $band }) =>
    $band
      ? `
        margin: ${space.lg}px -20px -20px;
        padding: 13px 20px;
        border-top: 1px solid ${line.hairline};
        background: ${colors.gray[25]};
      `
      : ""}

  button {
    min-width: 120px;
  }

  /* Stacks on a phone, not on a tablet — the same 480px the design system's
     own DialogActions uses, and the same as Form.Action, which this row is a
     near-copy of. At the old IPAD breakpoint the deposit dialog's two buttons
     stacked full-width in a 600px window while the rest of the product kept
     them side by side. */
  @media (max-width: 480px) {
    flex-direction: column-reverse;
    width: 100% !important;
    button {
      width: 100%;
      min-width: 0;
    }
  }
`;
